import { Component, OnInit } from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { CashRegisterEntity } from '../../../models/entity/cash-register/cash-register-entity';
import { CashRegisterService } from '../../../services/cash-register/cash-register.service';
import { CashRegisterSenderService } from '../../../services/cash-register/provision/cash-register-sender.service';
import { AlertifyService } from '../../../services/core/alertify.service';
import { FileService } from '../../../services/core/file.service';
import { ModalUtilsService } from '../../../services/core/modal-utils.service';
import { StorageConstants } from '../../../constant/storage.constant';
import { CashRegisterZReportBulkLocalRequest } from '../../../models/rest/cash-register/report/z-report/cash-register-z-report-bulk-local-request';
import { DateUtils } from '../../../utils/date.utils';
import { StoreSettingEntity } from '../../../models/entity/store/store-setting.entity';
import { StoreCacheService } from '../../../services/cache/store-cache.service';
import { AuthService } from '../../../services/auth.service';
import { StoreSettingService } from '../../../services/store/store-setting.service';
import { firstValueFrom } from 'rxjs';
import { CashRegisterVendorTypeEnumLocalValues, CashRegisterVendorTypeEnum, hasCashRegisterCapability } from '../../../enum/cash-register/cash-register-vendor-type-enum';
import { CashRegisterProvisionService } from '../../../services/cash-register/provision/cash-register-provision.service';
import { DeviceService } from '../../../services/store/device.service';
import { SessionStorageService } from '../../../services/session-storage.service';
import { DevicePreferenceTypeEnum } from '../../../enum/type/device-preference-type.enum';
import { DeviceLocalStorageData } from '../../../models/rest/user/device-local-storage-data.model';
import { DevicePreference } from '../../../models/entity/devicePreference';
import { PaymentTypeCashRegisterEnumValues, PaymentTypeEnumValues } from '../../../enum/type/payment-type.enum';
import { SelectedItemIdDto } from '../../../models/dto/selectedItemIdDto';
import { StoreService } from '../../../services/store/store.service';
import { StoreRelationTypeEnum } from '../../../enum/store-relation-type.enum';
import { ArrayUtils } from '../../../utils/array.utils';
import { StoreRelationUpdateRequest } from '../../../models/rest/store/store-relation-update-request.model';
import { CashRegisterAddUpdateComponent } from '../cash-register-add-update/cash-register-add-update.component';
import { ModalSizeEnum } from '../../../enum/modal-size.enum';
import { GeneralUtils } from '../../../utils/general.utils';
@Component({
    selector: 'app-cash-register-quick-access',
    templateUrl: './cash-register-quick-access.component.html',
    styleUrls: ['./cash-register-quick-access.component.css']
})
export class CashRegisterQuickAccessComponent implements OnInit {

    cashRegisters: CashRegisterEntity[] = [];
    storeSetting: StoreSettingEntity;
    selectedCashRegister: CashRegisterEntity;
    loading: boolean = false;
    isBusySave: boolean = false;
    isBusyOpenShift: boolean = false;
    isBusyCloseShift: boolean = false;
    isBusyXReport: boolean = false;
    isBusyPairDevice: boolean = false;
    isBusyClearBasket: boolean = false;
    paymentTypes: SelectedItemIdDto[] = [];
    selectedPaymentTypesForCashRegister: SelectedItemIdDto[] = [];
    paymentTypeDropdownSettings = {};
    isDefaultDeviceSelected: boolean = false;

    constructor(
        public activeModal: NgbActiveModal,
        private modalService: NgbModal,
        private cashRegisterService: CashRegisterService,
        private cashRegisterSenderService: CashRegisterSenderService,
        private alertifyService: AlertifyService,
        private fileService: FileService,
        private translate: TranslateService,
        private modalUtilsService: ModalUtilsService,
        private storeCacheService: StoreCacheService,
        private authService: AuthService,
        private storeSettingService: StoreSettingService,
        private deviceService: DeviceService,
        private sessionStorageService: SessionStorageService,
        private storeService: StoreService,
        private cashRegisterProvisionService: CashRegisterProvisionService
    ) { }

    ngOnInit() {
        this.getDropdownSettings();
        this.getPaymentTypes();
        this.initialoLoad();
    }
    async initialoLoad() {
        try {
            this.loading = true;
            await Promise.all([
                this.getStoreSetting(),
                this.getCashRegisters()
            ]);

            this.setDefaultCashRegister();
            this.syncDefaultDeviceSelection();
            this.getAllStoreRelations();
        } finally {
            this.loading = false;
        }
    }
    async getStoreSetting() {
        try {
            const res = await this.storeCacheService.getSetting(this.authService.getStoreId()).toPromise();
            this.storeSetting = res!;
        }
        catch (err) {
            this.alertifyService.error(err);
        }
    }

    async getCashRegisters() {
        try {
            const res = await this.storeCacheService.getCashRegistersByStoreId(this.authService.getStoreId()).toPromise();
            this.cashRegisters = res!;
        }
        catch (err) {
            this.alertifyService.error(err);
        }
    }

    setDefaultCashRegister() {
        const deviceLocalStorageData = this.sessionStorageService.getDeviceStorageData();
        const savedId = deviceLocalStorageData?.cashRegisterId;
        if (savedId && this.cashRegisters.length > 0) {
            this.selectedCashRegister = this.cashRegisters.find(cr => cr.id === savedId) || this.cashRegisters[0];
        } else if (this.cashRegisters.length > 0) {
            this.selectedCashRegister = this.cashRegisters[0];
        }
    }

    onSelectedCashRegisterChange() {
        this.syncDefaultDeviceSelection();
    }

    openEditCashRegister() {
        if (!this.selectedCashRegister) return;
        const modalRef = this.modalService.open(CashRegisterAddUpdateComponent, {
            backdrop: 'static', centered: true, scrollable: true, size: ModalSizeEnum.LARGE
        });
        modalRef.componentInstance.data = GeneralUtils.clone(this.selectedCashRegister);
        modalRef.componentInstance.passEntry.subscribe((receivedEntry) => {
            if (receivedEntry) {
                const idx = this.cashRegisters.findIndex(cr => cr.id === receivedEntry.id);
                if (idx >= 0) {
                    this.cashRegisters[idx] = receivedEntry;
                    this.cashRegisters = this.cashRegisters.slice();
                }
                this.selectedCashRegister = receivedEntry;
            }
            modalRef.close();
        });
    }

    syncDefaultDeviceSelection() {
        this.isDefaultDeviceSelected = this.isDefaultDevice;
    }

    get isDefaultDevice(): boolean {
        if (!this.selectedCashRegister) return false;
        const deviceLocalStorageData = this.sessionStorageService.getDeviceStorageData();
        const savedId = deviceLocalStorageData?.cashRegisterId;
        return savedId ? this.selectedCashRegister.id === savedId : false;
    }


    async save() {
        this.isBusySave = true;
        try {
            await Promise.all([
                this.saveCashRegister(),
                this.savePaymentTypesForCashRegister(),
                this.saveStoreSetting(),
                this.saveDefaultDevicePreference()
            ]);
            this.alertifyService.success(this.translate.instant('message.info.saved'));
        } catch (err) {
            this.alertifyService.error(err);
        } finally {
            this.isBusySave = false;
        }
    }

    async saveStoreSetting(): Promise<boolean> {
        if (!this.storeSetting) return false;
        try {
            const res = await firstValueFrom(this.storeSettingService.updateStoreSetting(this.storeSetting.id!, this.storeSetting));
            this.storeSetting = res;
            return true;
        } catch (err) {
            this.alertifyService.error(err);
            return false;
        }
    }

    async saveCashRegister() {
        if (!this.selectedCashRegister) return;

        try {
            await firstValueFrom(this.cashRegisterService.updateCashRegister(this.selectedCashRegister.id!, this.selectedCashRegister));
        } catch (err) {
            this.alertifyService.error(err);
        }
    }

    async saveDefaultDevicePreference(): Promise<void> {
        if (!this.selectedCashRegister) return;

        const deviceId = this.authService.getCurrenDeviceId();
        if (!deviceId) {
            this.alertifyService.error(this.translate.instant('message.error.device-not-found'));
            return;
        }

        let deviceLocalStorageData = this.sessionStorageService.getJSONData(StorageConstants.DEVICE_LOCAL_STORAGE) as DeviceLocalStorageData;
        if (!deviceLocalStorageData) {
            deviceLocalStorageData = new DeviceLocalStorageData();
        }

        if (this.isDefaultDeviceSelected) {
            deviceLocalStorageData.cashRegisterId = this.selectedCashRegister.id;
        } else {
            deviceLocalStorageData.cashRegisterId = 0;
        }

        this.sessionStorageService.remove(StorageConstants.DEVICE_LOCAL_STORAGE);
        this.sessionStorageService.setJSONData(StorageConstants.DEVICE_LOCAL_STORAGE, deviceLocalStorageData);

        await this.updateDevicePreference(deviceId, DevicePreferenceTypeEnum.DEVICE_LOCAL_STORAGE, JSON.stringify(deviceLocalStorageData));
    }

    async updateDevicePreference(deviceId: number, shortCode: string, value: string): Promise<void> {
        const devicePreference = new DevicePreference();
        devicePreference.deviceId = deviceId;
        devicePreference.shortCode = shortCode;
        devicePreference.value = value;
        await firstValueFrom(this.deviceService.updateDevicePreference(deviceId, devicePreference));
    }

    async openShift() {
        try {
            this.isBusyOpenShift = true;
            if (!this.selectedCashRegister) return;

            const confirmed = await this.modalUtilsService.openModalForConfirmation(
                this.translate.instant('title.confirmation'),
                this.translate.instant('confirmation.open-shift')
            );

            if (!confirmed) return;

            await this.cashRegisterSenderService.openShift(this.selectedCashRegister!.id!);
            this.alertifyService.success(this.translate.instant('message.info.process-completed-succesfully'));
        } catch (err) {
            this.alertifyService.error(err);
        } finally {
            this.isBusyOpenShift = false;
        }
    }

    async closeShift() {

        try {
            this.isBusyCloseShift = true;
            if (!this.selectedCashRegister) return;

            const confirmed = await this.modalUtilsService.openModalForConfirmation(
                this.translate.instant('title.confirmation'),
                this.translate.instant('confirmation.close-shift')
            );

            if (!confirmed) return;

            const res = await this.cashRegisterSenderService.closeShift(this.selectedCashRegister!.id!);
            this.alertifyService.success(this.translate.instant('message.info.process-completed-succesfully'));

            if (res?.receipt) {
                const pdfBlob = this.fileService.base64ToBlob(res.receipt.data);
                this.fileService.previewBLOB(pdfBlob);
            }
        } catch (err) {
            this.alertifyService.error(err);
        } finally {
            this.isBusyCloseShift = false;
        }
    }

    async getXReport() {


        try {
            this.isBusyXReport = true;

            if (!this.selectedCashRegister) return;

            const confirmed = await this.modalUtilsService.openModalForConfirmation(
                this.translate.instant('title.confirmation'),
                this.translate.instant('confirmation.get-x-report')
            );

            if (!confirmed) return;

            const res = await this.cashRegisterSenderService.getXReport(this.selectedCashRegister!.id!);
            this.alertifyService.success(this.translate.instant('message.info.process-completed-succesfully'));

            if (res?.receipt) {
                const pdfBlob = this.fileService.base64ToBlob(res.receipt.data);
                this.fileService.previewBLOB(pdfBlob);
            }
        } catch (err) {
            this.alertifyService.error(err);
        } finally {
            this.isBusyXReport = false;
        }
    }

    async getZReportBulk() {

        try {
            this.loading = true;
            const request = new CashRegisterZReportBulkLocalRequest();
            request.cashRegisterId = this.selectedCashRegister!.id!;
            request.startDate = DateUtils.addDays(DateUtils.getToday(), -31);
            request.endDate = DateUtils.getToday();

            const res = await this.cashRegisterSenderService.getZReport(request);
            this.alertifyService.success(this.translate.instant('message.info.process-completed-succesfully'));

            if (res?.receipt) {
                const pdfBlob = this.fileService.base64ToBlob(res.receipt.data);
                this.fileService.previewBLOB(pdfBlob);
            }
        } catch (err) {
            this.alertifyService.error(err);
        } finally {
            this.loading = false;
        }
    }

    async getControlTape() {
        if (!this.selectedCashRegister) return;

        try {
            this.loading = true;
            const res = await this.cashRegisterSenderService.getControlTape(this.selectedCashRegister!.id!);
            this.alertifyService.success(this.translate.instant('message.info.process-completed-succesfully'));

            if (res?.receipt) {
                const pdfBlob = this.fileService.base64ToBlob(res.receipt.data);
                this.fileService.previewBLOB(pdfBlob);
            }
        } catch (err) {
            this.alertifyService.error(err);
        } finally {
            this.loading = false;
        }
    }

    async pairDevice() {
        if (!this.selectedCashRegister) return;

        try {
            this.isBusyPairDevice = true;

            const confirmed = await this.modalUtilsService.openModalForConfirmation(
                this.translate.instant('title.confirmation'),
                this.translate.instant('confirmation.pair-device')
            );

            if (!confirmed) return;

            const res = await this.cashRegisterSenderService.pairDevice(this.selectedCashRegister!.id!);
            this.alertifyService.success(this.translate.instant('message.info.process-completed-succesfully'));

            if (res?.receipt) {
                const pdfBlob = this.fileService.base64ToBlob(res.receipt.data);
                this.fileService.previewBLOB(pdfBlob);
            }
        } catch (err) {
            this.alertifyService.error(err);
        } finally {
            this.isBusyPairDevice = false;
        }
    }

    isPavoVendor(): boolean {
        return hasCashRegisterCapability(this.selectedCashRegister?.vendorType, 'pairDevice');
    }

    isBekoVendor(): boolean {
        return this.selectedCashRegister?.vendorType === CashRegisterVendorTypeEnum.BEKO;
    }
    isOdealVendor(): boolean {
        return this.selectedCashRegister?.vendorType === CashRegisterVendorTypeEnum.ODEAL;
    }

    hasDeviceCodeField(): boolean {
        if (!this.selectedCashRegister?.vendorType) return false;
        const vt = this.selectedCashRegister.vendorType;
        return vt === CashRegisterVendorTypeEnum.ODEAL || vt === CashRegisterVendorTypeEnum.HUGIN || vt === CashRegisterVendorTypeEnum.PAVO || vt === CashRegisterVendorTypeEnum.BEKO;
    }

    async clearBasket() {
        if (!this.selectedCashRegister) return;
        try {
            this.isBusyClearBasket = true;
            const confirmed = await this.modalUtilsService.openModalForConfirmation(
                this.translate.instant('title.confirmation'),
                this.translate.instant('confirmation.clear-basket')
            );
            if (!confirmed) return;
            await firstValueFrom(this.cashRegisterProvisionService.clearBasket(this.selectedCashRegister.id!));
            this.alertifyService.success(this.translate.instant('message.info.process-completed-succesfully'));
        } catch (err) {
            this.alertifyService.error(err);
        } finally {
            this.isBusyClearBasket = false;
        }
    }

    hasOpenShift(): boolean {
        return hasCashRegisterCapability(this.selectedCashRegister?.vendorType, 'openShift');
    }

    hasCloseShift(): boolean {
        return hasCashRegisterCapability(this.selectedCashRegister?.vendorType, 'closeShift');
    }

    hasGetXReport(): boolean {
        return hasCashRegisterCapability(this.selectedCashRegister?.vendorType, 'getXReport');
    }

    isLocalVendor(): boolean {
        if (!this.selectedCashRegister || !this.selectedCashRegister.vendorType) {
            return false;
        }
        return CashRegisterVendorTypeEnumLocalValues.includes(this.selectedCashRegister.vendorType);
    }

    private getDropdownSettings() {
        this.paymentTypeDropdownSettings = {
            singleSelection: false,
            idField: "id",
            textField: "name",
            placeHolder: this.translate.instant('dropdown.select-payment-type'),
            selectAllText: this.translate.instant('dropdown.select-all-text'),
            unSelectAllText: this.translate.instant('dropdown.un-select-all-text'),
            searchPlaceholderText: this.translate.instant('dropdown.search-placeholder-text'),
            itemsShowLimit: 3,
            allowSearchFilter: true,
        };
    }

    private getPaymentTypes() {
        this.paymentTypes = [];
        PaymentTypeCashRegisterEnumValues.forEach(x => {
            let item = new SelectedItemIdDto();
            item.id = x;
            item.name = this.translate.instant('paymentTypeValue.' + x);
            this.paymentTypes.push(item);
        });
    }

    private getAllStoreRelations() {
        const storeId = this.authService.getStoreId();
        if (!storeId) return;

        this.storeService.getAllStoreRelations(storeId).subscribe(
            (res) => {
                this.selectedPaymentTypesForCashRegister = res.paymentTypesForCashRegister ?? [];
            },
            (err) => {
                this.alertifyService.error(err);
            }
        );
    }

    private getItemForModify(storeId: number, relationType: string, all: number[], granted: number[]): StoreRelationUpdateRequest {
        const item = new StoreRelationUpdateRequest();
        item.storeId = storeId;
        item.relationType = relationType;
        item.all = all;
        item.granted = granted;
        return item;
    }

    private async savePaymentTypesForCashRegister() {
        const storeId = this.authService.getStoreId();
        if (!storeId) return;

        const requestList: StoreRelationUpdateRequest[] = [];
        requestList.push(
            this.getItemForModify(
                storeId,
                StoreRelationTypeEnum.PAYMENT_TYPE_CASH_REGISTER,
                ArrayUtils.getValuesByKey(this.paymentTypes, 'id'),
                ArrayUtils.getValuesByKey(this.selectedPaymentTypesForCashRegister, 'id')
            )
        );

        await firstValueFrom(this.storeService.storeRelationUpdate(requestList));
    }

    close() {
        this.activeModal.close();
    }
}

